<template>
  <div class="main-container">
    <div>
      <!-- Header Row -->
      <v-row class="pa-2 header-row">
        <v-col cols="12" class="d-flex justify-space-between align-center">
          <v-btn icon class="back-button" @click="confirmBackNavigation">
            <v-icon class="white--text">mdi-arrow-left</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loader while fetching data -->
      <v-row v-if="loading" justify="center" align="center" class="loader-row">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-row>

      <!-- Display POs after data is fetched -->
      <div v-if="!loading" class="po-container">
        <!-- Search Field -->
        <v-row justify="center" class="search-filter-row">
          <v-col cols="12" md="4">
            <v-text-field
              class="pa-3 white-search-field"
              filled
              dense
              rounded
              v-model="search"
              append-icon="mdi-magnify"
              label="Search POs"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- PO Cards -->
        <v-row>
          <v-col 
            cols="12" 
            sm="6" 
            md="4" 
            lg="3" 
            v-for="(po, index) in filteredPOs" 
            :key="index"
          >
            <v-card
              class="modern-card"
              elevation="6"
              hover
              @click="handleCardClick(po)"
            >
              <!-- Header with Status Square, Status Chip, Purchase Number Chip -->
              <v-row class="d-flex justify-end pr-3 pt-3">
                <!-- Status Square -->
                <div
                  :class="['urgency-square', getStatusSquareColor(po.status)]"
                ></div>

                <!-- Status Chip -->
                <v-chip
                  :color="getChipColor(po.status)"
                  dark
                  label
                  class="status-chip ml-2"
                >
                  {{ po.status || 'Unknown' }}
                </v-chip>

                <!-- Purchase Number Chip -->
                <v-chip
                  color="grey"
                  dark
                  label
                  class="purchase-number-chip ml-2"
                >
                  No.{{ po.purchaseNumber || 'Unknown' }}
                </v-chip>
              </v-row>

              <!-- Card Content -->
              <v-card-text class="pa-4">
                <!-- Category (or Priority) -->
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Category:</span>
                    <span class="font-weight-medium">{{ po.category || 'Unknown' }}</span>
                  </v-col>
                </v-row>

                <!-- Supplier -->
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Supplier:</span>
                    <span class="font-weight-medium">{{ po.supplier || 'Unknown' }}</span>
                  </v-col>
                </v-row>
              </v-card-text>

              <!-- Card Actions -->
              <v-card-actions>
                <v-btn
                  width="100%"
                  rounded
                  dark
                  color="#ff3b47"
                  @click.stop="openPO(po)"
                  class="open-po-button"
                >
                  Edit PO
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Confirmation Dialog for Back Button -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="green" text @click="goBack">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      pos: [],             // Array of POs
      search: '',          // For searching
      userId: '',          // UID of current user
      userRole: '',        // Role of current user
      loading: true,       // Controls the loader
      backDialog: false,   // Controls the back confirmation dialog
    };
  },
  computed: {
    filteredPOs() {
      // First, filter out completed POs.
      let incompletePOs = this.pos.filter(po => {
        const status = po.status ? po.status.toLowerCase() : '';
        return status !== 'complete' && status !== 'completed';
      });

      // Next, apply the search filter if needed.
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        incompletePOs = incompletePOs.filter(po =>
          (po.purchaseNumber && po.purchaseNumber.toString().toLowerCase().includes(searchLower)) ||
          (po.category && po.category.toLowerCase().includes(searchLower)) ||
          (po.supplier && po.supplier.toLowerCase().includes(searchLower)) ||
          (po.status && po.status.toLowerCase().includes(searchLower))
        );
      }

      // Finally, sort by purchaseNumber descending.
      incompletePOs.sort((a, b) => b.purchaseNumber - a.purchaseNumber);
      return incompletePOs;
    },
  },
  async created() {
    await this.getCurrentUser(); // Get logged-in user info
    await this.fetchUserPOs();   // Fetch user-specific POs
  },
  methods: {
    /**
     * Fetch current user and their role from Firestore.
     */
    async getCurrentUser() {
      const user = auth.currentUser;
      if (user) {
        this.userId = user.uid;
        // Query 'users' collection to find user doc by UID
        const userQuery = query(collection(db, "users"), where("uid", "==", this.userId));
        const userSnapshot = await getDocs(userQuery);
        if (!userSnapshot.empty) {
          this.userRole = userSnapshot.docs[0].data().role || '';
        }
      }
    },

    /**
     * Fetch POs from Firestore based on the current user.
     * (If needed, you can adjust these queries. Here, everyone is allowed to open their POs.)
     */
    async fetchUserPOs() {
      if (!this.userId) return;

      let poQuery;
      if (this.userRole === "Engineer") {
        poQuery = query(
          collection(db, "purchaseOrders"),
          where("engineerUID", "==", this.userId)
        );
      } else if (this.userRole === "Salesperson") {
        poQuery = query(
          collection(db, "purchaseOrders"),
          where("salespersonUID", "==", this.userId)
        );
      } else {
        // For any other role, filter by the creator
        poQuery = query(
          collection(db, "purchaseOrders"),
          where("createdBy", "==", this.userId)
        );
      }

      const querySnapshot = await getDocs(poQuery);
      const pos = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        pos.push({
          id: docSnap.id,
          purchaseNumber: data.purchaseNumber || 'Unknown',
          category: data.priority || 'Unknown Category',
          supplier: data.supplier || 'Unknown Supplier',
          // Make sure your Firestore docs have a 'status' field for color-coding
          status: data.status || 'Unknown',
        });
      });

      // Sort by purchaseNumber descending
      pos.sort((a, b) => b.purchaseNumber - a.purchaseNumber);
      this.pos = pos;
      this.loading = false;
    },

    /**
     * Show confirmation dialog for back navigation.
     */
    confirmBackNavigation() {
      this.backDialog = true;
    },

    /**
     * Navigate back in history.
     */
    goBack() {
      this.backDialog = false;
      this.$router.go(-1);
    },

    /**
     * Handle card click: open the PO.
     */
    handleCardClick(po) {
      this.openPO(po);
    },

    /**
     * Open the PO for viewing/editing.
     * This method stores the selected PO ID and navigates to the EditPO page.
     */
    openPO(po) {
      if (po.id) {
        localStorage.setItem('selectedPO', po.id);
        this.$router.push({ name: 'EditPO' });
      }
    },

    /**
     * Get CSS class for colored square based on PO status.
     */
    getStatusSquareColor(status) {
      if (!status) return 'grey-square';
      switch (status.toLowerCase()) {
        case 'completed':
        case 'complete':
          return 'green-square';
        case 'pending':
          return 'orange-square';
        case 'delayed':
          return 'red-square';
        case 'in progress':
          return 'purple-square';
        default:
          return 'grey-square';
      }
    },

    /**
     * Get Vuetify chip color based on PO status.
     */
    getChipColor(status) {
      if (!status) return 'grey';
      switch (status.toLowerCase()) {
        case 'completed':
        case 'complete':
          return 'green';
        case 'pending':
          return 'orange';
        case 'delayed':
          return 'red';
        case 'in progress':
          return 'purple';
        default:
          return 'grey';
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

/* Global Font */
body, div, span, v-card-title, v-card-text, v-row, v-col, v-card, v-chip, v-avatar {
  font-family: 'Montserrat', sans-serif;
}

/* Main Container with Dark Background */
.main-container {
  background-color: #000; /* Black background */
  min-height: 100vh;
}

/* Header Row */
.header-row {
  background-color: #1e1e1e;
  border-bottom: 1px solid #333;
}

/* PO Container: White with Rounded Corners */
.po-container {
  background-color: #ffffff;
  border-radius: 20px 20px 0 0;
  padding: 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  height: 70%;
}

/* Loader Row */
.loader-row {
  margin-top: 50px;
}

/* Search Filter Row */
.search-filter-row {
  margin-bottom: 24px;
}

/* Modern Card */
.modern-card {
  background-color: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  margin: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.modern-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

/* Urgency / Status Square */
.urgency-square {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

/* Status Square Colors */
.red-square {
  background-color: red;
}
.orange-square {
  background-color: orange;
}
.green-square {
  background-color: green;
}
.purple-square {
  background-color: purple;
}
.grey-square {
  background-color: grey;
}

/* Status Chip */
.status-chip {
  font-size: 0.75rem;
  text-transform: capitalize;
}

/* Purchase Number Chip */
.purchase-number-chip {
  font-size: 0.75rem;
  text-transform: uppercase;
}

/* Card Text & Typography */
.pa-4 {
  padding: 16px;
}
.text-caption {
  font-size: 0.8rem;
  color: #757575;
}
.font-weight-medium {
  font-weight: 500;
  color: #333;
}

/* Open PO Button Styling */
.open-po-button {
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.3s ease;
}
.open-po-button:hover {
  background-color: #e02834; /* Slightly darker than #ff3b47 */
  transform: scale(1.02);
}

/* Back Button */
.back-button {
  color: white;
  border-radius: 50%;
  transition: transform 0.3s ease-in-out;
}
.back-button:hover {
  transform: scale(1.1);
}
.back-button .v-icon {
  color: white;
}

/* White Search Field */
.white-search-field .v-input__control {
  background-color: white;
  border-radius: 8px;
}
.white-search-field .v-label {
  color: #555;
}
.white-search-field .v-input__icon--append .v-icon {
  color: #555;
}

/* Dialog Styling */
.v-dialog .v-card {
  border-radius: 16px;
}
.v-card-title {
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
}
.v-card-actions {
  padding: 16px;
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .modern-card {
    margin: 8px;
    height: auto;
  }
  .v-col {
    margin-bottom: 16px;
  }
}
</style>
